/* eslint react/prop-types: 0 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { reduxForm, Field, initialize } from 'redux-form';
import { compose, setDisplayName, setPropTypes } from 'recompose';
import { showError } from 'rp-auth';
import { v4 as uuid } from 'uuid';
import Pointer from './Pointer';
import Details from './Details';
import PaymentDetails from './PaymentDetails';
import Summary from './Summary';
import Lightbox from '../../../common/Lightbox';
import PaymentConfirmation from '../../../PaymentPage/PaymentWizard/PaymentConfirmation';
import {
  subscriptionUpgradeReset,
  subscriptionRenewReset,
  subscriptionPick,
} from '../../../../actions/subscriptions';
import { loadAccountPageRequest } from '../../actions';
import { withBreakpoint } from '../../../../enhancers';
import { loadUserAmendRequest } from '../../../../actions/userInfo';
import { closeLightbox } from '../../../../actions/lightbox';
import {
  getServicePriceId,
  getServicePeriod,
  getServicePrice,
  getCurrentSubscription,
  getServiceTitle,
  getServicePayment,
} from '../../../../selectors';
import paramValueByName from '../../../../helpers/paramValueByName';
import styles from './SubscriptionUpgrade.scss';

const SUBSCRIPTION_UPGRADE_FORM = 'upgrade';
const defaultUserServices = {
  customParameters: {
    RP_newsletterRP: false,
    RP_18yo: true,
    RP_newsletterThirdParty: false,
    RP_horseBetting: false,
    RP_horseNews: false,
    RP_bloodstock: false,
    RP_sportBetting: false,
    RP_competitions: false,
    membersBriefing: false,
    theFrontRunner: false,
    theUltimateDaily: false,
    subscriberRewards: false,
  },
};

const renderField = ({ input, label, type }) => {
  const id = uuid();
  return (
    <div className={styles.field}>
      <input {...input} placeholder={label} type={type} className={styles[type]} id={id} />
      <label
        htmlFor={id}
        className={classNames(styles.label, { [styles.checked]: input.checked })}
      >
        <span className={styles.title}>{label}</span>
      </label>
    </div>
  );
};

const required = value => (value ? undefined : 'Required');

function handleUpgrade(values, dispatch, {
  action,
  currentService: currentServiceId,
  serviceToPick,
}) {
  const config = {
    conditions: {
      subscriberRewards: values.subscriberRewards,
      termsConditions: values.termsConditions,
    },
  };

  dispatch(subscriptionPick(serviceToPick));
  dispatch(loadUserAmendRequest(config));
  dispatch(action({
    currentServiceId,
    servicePriceId: serviceToPick ? serviceToPick.priceId : null,
    serviceId: serviceToPick ? serviceToPick.id : null,
  }));
}

class SubscriptionUpgrade extends Component {
  static getDerivedStateFromProps(nextProps) {
    SubscriptionUpgrade.shouldShowError(nextProps);
    return null;
  }

  static shouldShowError({ upgradeStatus, addStatus, errorMessage = '' }) {
    if (errorMessage && (!upgradeStatus || !addStatus)) {
      showError(errorMessage);
    }
  }

  getConfirmationMessage = () => {
    const MESSAGE_UPGRADE = 'Thank you for upgrading';
    const MESSAGE_RENEW = 'Thank you for subscribing';

    return this.props.addStatus
      ? MESSAGE_RENEW
      : this.props.upgradeStatus
        ? MESSAGE_UPGRADE
        : MESSAGE_RENEW;
  };

  handleModalClose = () => {
    this.props.subscriptionUpgradeReset();
    this.props.closeLightbox();
  };

  handlePaymentConfirmationClose = () => {
    this.props.subscriptionUpgradeReset();
    this.props.loadAccountPageRequest();
    this.handleModalClose();
  };

  computePointerPosition = () => {
    const parentElement = findDOMNode(this).parentElement;
    const parentRect = parentElement.getBoundingClientRect();
    const offsetParentRect = parentElement.offsetParent.getBoundingClientRect();
    const leftCoordinate = parentRect.left + (parentRect.width / 2) - offsetParentRect.left - 24;

    return {
      left: leftCoordinate,
    };
  };

  render() {
    return (
      <form
        name={SUBSCRIPTION_UPGRADE_FORM}
        className={classNames(styles.root, styles[this.props.type])}
        onSubmit={this.props.handleSubmit}
      >
        <Pointer calculatePosition={this.computePointerPosition} />
        <div className={styles.description}>
          <Details
            payment={this.props.servicePayment}
            price={this.props.servicePrice}
            period={this.props.servicePeriod}
          />
          <p className={styles.info}>
              Racing Post Customer Services operates 7 days a week
          </p>
        </div>
        <PaymentDetails expired={false} card={this.props.creditCard} />
        <Field
          name="subscriberRewards"
          type="checkbox"
          component={renderField}
          label="I would like to collect Subscriber rewards points with this purchase
                 that I can redeem against raceday tickets and other great offers"
        />
        <Field
          name="termsConditions"
          type="checkbox"
          component={renderField}
          validate={[required]}
          label={<span>I accept the <a className="ui-link_secondary">Terms and Conditions</a></span>}
        />
        <Summary
          title={this.props.serviceTitle}
          label={this.props.type}
          fetching={this.props.fetching}
          price={this.props.servicePrice}
          form={SUBSCRIPTION_UPGRADE_FORM}
          disabled={!this.props.valid}
        />
        <Lightbox
          open={this.props.lightbox}
          onClose={this.handleModalClose}
          className={styles.modal}
        >
          <PaymentConfirmation
            confirmationMessage={this.getConfirmationMessage()}
            onClick={this.handlePaymentConfirmationClose}
            modal
          />
        </Lightbox>
      </form>
    );
  }
}

function mapStateToProps(state, { service }) {
  const userServices = Object.assign({}, defaultUserServices, state.userServices.data);
  const currentPackage = state.userServices.data !== null ? getCurrentSubscription(state) : null;

  return {
    initialValues: {
      subscriberRewards: paramValueByName(userServices, 'subscriberRewards'),
      termsConditions: true,
    },
    currentService: currentPackage.accountSubscriptionInfo.recurringPaymentInfo.resourceReference,
    servicePeriod: getServicePeriod(state),
    servicePrice: getServicePrice(state),
    servicePriceId: getServicePriceId(state),
    serviceTitle: getServiceTitle(state),
    servicePayment: getServicePayment(state),
    creditCard: state.userInfo.data !== null ? state.userInfo.data.CreditCard : {},
    fetching: state.userInfo.fetching || state.subscriptions.fetching,
    upgradeStatus: state.subscriptions.upgrade,
    addStatus: state.subscriptions.add,
    errorMessage: state.subscriptions.error,
    lightbox: state.lightbox,
    serviceToPick: currentPackage.upgradeTo ?
      currentPackage.upgradeTo.find(option => option.id === service) :
      currentPackage,
  };
}

export default compose(
  setDisplayName('SubscriptionUpgrade'),
  setPropTypes({
    lightbox: PropTypes.bool.isRequired,
  }),
  withBreakpoint,
  connect(mapStateToProps, {
    initialize,
    subscriptionUpgradeReset,
    subscriptionRenewReset,
    closeLightbox,
    subscriptionPick,
    loadAccountPageRequest,
  }),
  reduxForm({
    form: SUBSCRIPTION_UPGRADE_FORM,
    enableReinitialize: true,
    destroyOnUnmount: false,
    onSubmit: handleUpgrade,
  }),
)(SubscriptionUpgrade);
